import { REPEAT_MODE } from '../constants/repeatTypes'

const buildDailyRrule = (interval, count) => {
  const parts = [`FREQ=DAILY;INTERVAL=${interval || 1}`]
  if (count) parts.push(`COUNT=${count}`)
  return parts.join(';')
}

const parseRrulePart = (rrule, key) => {
  if (!rrule) return null
  const segment = String(rrule)
    .split(';')
    .find(part => part.startsWith(`${key}=`))
  return segment ? segment.split('=')[1] : null
}

export const normalizeDaysOfMonth = repeat => {
  if (Array.isArray(repeat?.daysOfMonth) && repeat.daysOfMonth.length) {
    return repeat.daysOfMonth.map(Number).filter(day => day >= 1 && day <= 31)
  }
  const single = Number(repeat?.dayOfMonth)
  if (single >= 1 && single <= 31) return [single]
  return [1]
}

export const mapRepeatToApi = (repeat = {}) => {
  const mode = repeat.mode || REPEAT_MODE.doesNotRepeat

  if (mode === REPEAT_MODE.doesNotRepeat || mode === REPEAT_MODE.oncePerAnchorOccurrence) {
    return { mode, rrule: null }
  }

  if (mode === REPEAT_MODE.episodicOnSubmission) {
    const offset = repeat.offsetAfterCompletion || {}
    return {
      mode,
      rrule: null,
      offset_after_completion: {
        value: offset.value ?? offset.amount ?? 0,
        unit: offset.unit || 'hours',
      },
    }
  }

  if (mode === REPEAT_MODE.everyNDays) {
    const interval = repeat.intervalDays ?? repeat.ui?.interval ?? 1
    const recurrences = repeat.recurrences ?? repeat.ui?.recurrences
    return {
      mode: 'rrule',
      ui: {
        scheme: 'every_n_days',
        interval,
        interval_type: 'days',
        recurrences,
      },
      rrule: buildDailyRrule(interval, recurrences),
      dtstart_from_anchor: true,
    }
  }

  if (mode === REPEAT_MODE.weeklyOnSelectedDays) {
    const weekdays = (repeat.weekdays || ['MO']).map(String).filter(Boolean)
    const byDay = weekdays.join(',')
    const recurrences = repeat.recurrences ?? repeat.ui?.recurrences
    const rruleParts = [`FREQ=WEEKLY`, `BYDAY=${byDay}`, 'INTERVAL=1']
    if (recurrences) rruleParts.push(`COUNT=${recurrences}`)
    return {
      mode: 'rrule',
      ui: {
        scheme: 'weekly_on_selected_days',
        weekdays,
        recurrences,
      },
      rrule: rruleParts.join(';'),
      dtstart_from_anchor: true,
    }
  }

  if (mode === REPEAT_MODE.everyNWeeks) {
    const interval = repeat.intervalWeeks ?? repeat.ui?.interval ?? 1
    const recurrences = repeat.recurrences ?? repeat.ui?.recurrences
    const rruleParts = [`FREQ=WEEKLY`, `INTERVAL=${interval || 1}`]
    if (recurrences) rruleParts.push(`COUNT=${recurrences}`)
    return {
      mode: 'rrule',
      ui: {
        scheme: 'every_n_weeks',
        interval,
        interval_type: 'weeks',
        recurrences,
      },
      rrule: rruleParts.join(';'),
      dtstart_from_anchor: true,
    }
  }

  if (mode === REPEAT_MODE.everyNHours) {
    const interval = repeat.intervalHours ?? repeat.ui?.interval ?? 1
    const recurrences = repeat.recurrences ?? repeat.ui?.recurrences
    const rruleParts = [`FREQ=HOURLY`, `INTERVAL=${interval || 1}`]
    if (recurrences) rruleParts.push(`COUNT=${recurrences}`)
    return {
      mode: 'rrule',
      ui: {
        scheme: 'every_n_hours',
        interval,
        interval_type: 'hours',
        recurrences,
      },
      rrule: rruleParts.join(';'),
      dtstart_from_anchor: true,
    }
  }

  if (mode === REPEAT_MODE.everyNMinutes) {
    const interval = repeat.intervalMinutes ?? repeat.ui?.interval ?? 1
    const recurrences = repeat.recurrences ?? repeat.ui?.recurrences
    const rruleParts = [`FREQ=MINUTELY`, `INTERVAL=${interval || 1}`]
    if (recurrences) rruleParts.push(`COUNT=${recurrences}`)
    return {
      mode: 'rrule',
      ui: {
        scheme: 'every_n_minutes',
        interval,
        interval_type: 'minutes',
        recurrences,
      },
      rrule: rruleParts.join(';'),
      dtstart_from_anchor: true,
    }
  }

  if (mode === REPEAT_MODE.everyNMonths) {
    const months = repeat.months ?? repeat.ui?.interval ?? 1
    const recurrences = repeat.recurrences ?? repeat.ui?.recurrences
    const rruleParts = [`FREQ=MONTHLY`, `INTERVAL=${months || 1}`]
    if (recurrences) rruleParts.push(`COUNT=${recurrences}`)
    return {
      mode: 'rrule',
      ui: {
        scheme: 'every_n_months',
        interval: months,
        interval_type: 'months',
        recurrences,
      },
      rrule: rruleParts.join(';'),
      dtstart_from_anchor: true,
    }
  }

  if (mode === REPEAT_MODE.monthlyOnDay) {
    const days = normalizeDaysOfMonth(repeat)
    const months = repeat.months ?? 1
    const byMonthDay = days.join(',')
    const recurrences = repeat.recurrences ?? repeat.ui?.recurrences
    const rruleParts = [`FREQ=MONTHLY`, `INTERVAL=${months}`, `BYMONTHDAY=${byMonthDay}`]
    if (recurrences) rruleParts.push(`COUNT=${recurrences}`)
    return {
      mode: 'rrule',
      ui: {
        scheme: 'monthly_on_day',
        interval: months,
        interval_type: 'months',
        day_of_month: days.length === 1 ? days[0] : undefined,
        days_of_month: days,
        recurrences,
      },
      rrule: rruleParts.join(';'),
      dtstart_from_anchor: true,
    }
  }

  if (mode === 'rrule') {
    return { mode, rrule: repeat.rrule ?? null, ui: repeat.ui }
  }

  return { mode, rrule: null }
}

const modeFromRruleScheme = scheme => {
  switch (scheme) {
    case 'monthly_on_day':
      return REPEAT_MODE.monthlyOnDay
    case 'weekly_on_selected_days':
      return REPEAT_MODE.weeklyOnSelectedDays
    case 'every_n_weeks':
      return REPEAT_MODE.everyNWeeks
    case 'every_n_hours':
      return REPEAT_MODE.everyNHours
    case 'every_n_minutes':
      return REPEAT_MODE.everyNMinutes
    case 'every_n_months':
      return REPEAT_MODE.everyNMonths
    case 'every_n_days':
    default:
      return REPEAT_MODE.everyNDays
  }
}

export const mapRepeatFromApi = (repeat = {}, fallback = {}) => {
  const ui = repeat.ui || {}
  const isRrule = repeat.mode === 'rrule'
  const scheme = ui.scheme
  let mode = repeat.mode || fallback.mode
  if (isRrule) {
    mode = modeFromRruleScheme(scheme)
  }

  const byDay = parseRrulePart(repeat.rrule, 'BYDAY')
  const byMonthDay = parseRrulePart(repeat.rrule, 'BYMONTHDAY')
  const intervalFromRrule = parseRrulePart(repeat.rrule, 'INTERVAL')

  const daysOfMonth =
    ui.days_of_month ||
    (Array.isArray(ui.day_of_month) ? ui.day_of_month : null) ||
    (byMonthDay ? byMonthDay.split(',').map(Number) : null) ||
    fallback.daysOfMonth

  return {
    ...fallback,
    mode,
    intervalDays: ui.interval ?? repeat.every_n_days ?? repeat.interval_days ?? fallback.intervalDays,
    intervalWeeks:
      scheme === 'every_n_weeks'
        ? ui.interval ?? intervalFromRrule ?? fallback.intervalWeeks
        : fallback.intervalWeeks,
    intervalHours:
      scheme === 'every_n_hours'
        ? ui.interval ?? intervalFromRrule ?? fallback.intervalHours
        : fallback.intervalHours,
    intervalMinutes:
      scheme === 'every_n_minutes'
        ? ui.interval ?? intervalFromRrule ?? fallback.intervalMinutes
        : fallback.intervalMinutes,
    months:
      ui.interval_type === 'months' || scheme === 'every_n_months' || scheme === 'monthly_on_day'
        ? ui.interval ?? intervalFromRrule ?? repeat.months ?? fallback.months
        : repeat.months ?? fallback.months,
    dayOfMonth: daysOfMonth?.length === 1 ? daysOfMonth[0] : ui.day_of_month ?? repeat.day_of_month ?? fallback.dayOfMonth,
    daysOfMonth: daysOfMonth || (fallback.dayOfMonth ? [fallback.dayOfMonth] : [1]),
    weekdays:
      ui.weekdays || (byDay ? byDay.split(',') : null) || fallback.weekdays || ['MO'],
    recurrences: ui.recurrences ?? fallback.recurrences,
    chapterIntervals: repeat.chapter_intervals || fallback.chapterIntervals,
    offsetAfterCompletion: repeat.offset_after_completion
      ? {
          value: repeat.offset_after_completion.value,
          unit: repeat.offset_after_completion.unit || 'hours',
        }
      : fallback.offsetAfterCompletion,
  }
}
